import React, { useEffect, useState } from "react";
import { FiArrowRight, FiClock, FiShield, FiTag, FiX } from "react-icons/fi";
import { sanitizeImageUrl } from "../../utils/imageUtils";
import "../../styles/promo-banner-view.css";

/**
 * One banner, rendered identically in the admin preview and on the storefront.
 * Pure presentation: it receives the banner config and its linked promotion.
 *
 * Optional `brand` prop: { logoUrl, name } – shown at the top of the "Split"
 * popup layout.
 */

export const BANNER_THEMES = {
  brand: { label: "Brand", bg: "#2c7da0", fg: "#ffffff" },
  ink: { label: "Midnight", bg: "#1e293b", fg: "#ffffff" },
  sale: { label: "Sale", bg: "#b91c1c", fg: "#ffffff" },
  forest: { label: "Forest", bg: "#166534", fg: "#ffffff" },
  sand: { label: "Sand", bg: "#fef3c7", fg: "#78350f" },
};

export const POPUP_LAYOUTS = ["Split", "Background", "ImageLeft", "ImageRight"];

export const getBannerColors = (banner) => {
  if (banner.Theme === "custom") {
    return {
      bg: banner.BackgroundColor || "#2c7da0",
      fg: banner.TextColor || "#ffffff",
    };
  }
  return BANNER_THEMES[banner.Theme] || BANNER_THEMES.brand;
};

export const getDiscountText = (promo, formatCurrency = (v) => v) => {
  if (!promo) return "";
  if (promo.DiscountType === "Percentage") return `${promo.DiscountValue}% off`;
  if (promo.DiscountType === "FixedAmount")
    return `${formatCurrency(promo.DiscountValue)} off`;
  return "Special offer";
};

// "Ends in 3d 4h" – promotion end dates are date-only, so count to end of day.
const useCountdown = (endDate, enabled) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!enabled || !endDate) return undefined;
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, [enabled, endDate]);

  if (!enabled || !endDate) return null;
  const end = new Date(endDate);
  if (Number.isNaN(end.getTime())) return null;
  end.setHours(23, 59, 59, 999);

  const ms = end.getTime() - now;
  if (ms <= 0) return null;
  const mins = Math.floor(ms / 60000);
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);

  if (days >= 1) return `Ends in ${days}d ${hours}h`;
  if (hours >= 1) return `Ends in ${hours}h ${mins % 60}m`;
  return `Ends in ${mins}m`;
};

const PromoBannerView = ({
  banner,
  promotion,
  onDismiss,
  preview = false,
  brand,
}) => {
  const { bg, fg } = getBannerColors(banner);
  const countdown = useCountdown(promotion?.EndDate, banner.ShowCountdown);
  const isHero = banner.Placement === "Hero";
  const isPopup = banner.Placement === "Popup";
  const requestedImage =
    banner.ImageSource === "Product" ? banner.ProductImageUrl : banner.ImageUrl;
  const imageUrl = requestedImage ? sanitizeImageUrl(requestedImage) : null;
  const logoUrl = brand?.logoUrl ? sanitizeImageUrl(brand.logoUrl) : null;
  const showCode = banner.ShowCode && promotion?.Code;
  const canClose = (banner.IsDismissible || isPopup) && Boolean(onDismiss);
  const requestedLink =
    typeof banner.ButtonLink === "string" ? banner.ButtonLink : "#";
  const buttonLink =
    (requestedLink.startsWith("/") && !requestedLink.startsWith("//")) ||
    /^https?:\/\//i.test(requestedLink)
      ? requestedLink
      : "#";

  const ctaText = isPopup ? "Shop now" : banner.ButtonText;
  const trustText =
    banner.TrustText === undefined || banner.TrustText === null
      ? "Trusted. Secure. Delivered."
      : banner.TrustText.trim();
  const badgeText =
    banner.BadgeText === undefined || banner.BadgeText === null
      ? "Limited time offer"
      : banner.BadgeText.trim();
  const popupLayout = POPUP_LAYOUTS.includes(banner.PopupLayout)
    ? banner.PopupLayout
    : "Split";
  const isSplit = isPopup && popupLayout === "Split";
  const textPosition = ["Left", "Center", "Right"].includes(banner.TextPosition)
    ? banner.TextPosition
    : "Right";
  const imagePosition = [
    "Center",
    "Top",
    "Bottom",
    "Left",
    "Right",
    "Top left",
    "Top right",
    "Bottom left",
    "Bottom right",
  ].includes(banner.ImagePosition)
    ? banner.ImagePosition.toLowerCase()
    : "center";
  const rawTextX = Math.min(100, Math.max(0, Number(banner.TextX ?? 88)));
  const popupTextX =
    popupLayout === "ImageLeft"
      ? 45 + rawTextX * 0.55
      : popupLayout === "ImageRight"
        ? rawTextX * 0.55
        : rawTextX;
  const popupTextY = Math.min(100, Math.max(0, Number(banner.TextY ?? 72)));
  const popupTextSize = Math.min(
    32,
    Math.max(12, Number(banner.TextSize ?? 18)),
  );

  // "Get 15% Off | Your First Order" -> accent first line, dark second line.
  const [accentLine, ...restLines] = (banner.Headline || "").split("|");
  const secondLine = restLines.join("|").trim();

  const cta = ctaText ? (
    <a
      className="pbv-cta"
      href={buttonLink}
      onClick={preview ? (e) => e.preventDefault() : undefined}
      tabIndex={preview ? -1 : undefined}
    >
      {ctaText}
      {isPopup && <FiArrowRight aria-hidden="true" />}
    </a>
  ) : null;

  const extras = (
    <>
      {showCode && <code className="pbv-code">{promotion.Code}</code>}
      {countdown && (
        <span className="pbv-countdown">
          <FiClock aria-hidden="true" /> {countdown}
        </span>
      )}
    </>
  );

  const closeButton = canClose ? (
    <button
      type="button"
      className="pbv-close"
      aria-label="Dismiss banner"
      onClick={onDismiss}
    >
      <FiX />
    </button>
  ) : null;

  const popupCode = showCode && (
    <div className="pbv-popup-code">
      {isSplit && <FiTag aria-hidden="true" />}
      <span>Use code:</span>
      <strong>{promotion.Code}</strong>
    </div>
  );

  const popupTrust = trustText && (
    <div className="pbv-popup-trust">
      <FiShield aria-hidden="true" />
      <span>{trustText}</span>
    </div>
  );

  return (
    <div className="pbv-frame">
      <div
        className={`pbv ${isPopup ? "pbv-popup" : isHero ? "pbv-hero" : "pbv-topbar"}`}
        style={{
          "--pbv-bg": bg,
          "--pbv-fg": fg,
          "--pbv-image-position": imagePosition,
          "--pbv-popup-text-x": `${popupTextX}%`,
          "--pbv-popup-text-y": `${popupTextY}%`,
          "--pbv-popup-text-size": `${popupTextSize}px`,
          "--pbv-popup-text-color":
            banner.PopupTextColor ||
            (popupLayout === "Background" ? "#ffffff" : "#344256"),
        }}
        data-popup-layout={isPopup ? popupLayout : undefined}
        data-text-position={isPopup ? textPosition : undefined}
      >
        {isSplit ? (
          <>
            <div className="pbv-split-main">
              {(logoUrl || brand?.name) && (
                <div className="pbv-split-brand">
                  {logoUrl && <img src={logoUrl} alt="" loading="lazy" />}
                  {!logoUrl && brand?.name && <span>{brand.name}</span>}
                </div>
              )}
              {badgeText && (
                <span className="pbv-split-badge">{badgeText}</span>
              )}
              {banner.Headline && (
                <h2 className="pbv-split-headline">
                  <span className="pbv-split-accent">{accentLine.trim()}</span>
                  {secondLine && <span>{secondLine}</span>}
                </h2>
              )}
              {banner.Subtext && (
                <p className="pbv-split-subtext">{banner.Subtext}</p>
              )}
              {popupCode}
              {countdown && (
                <span className="pbv-countdown">
                  <FiClock aria-hidden="true" /> {countdown}
                </span>
              )}
              {cta}
              {popupTrust}
            </div>
            <div className="pbv-split-media" aria-hidden="true">
              {imageUrl ? <img src={imageUrl} alt="" loading="lazy" /> : null}
            </div>
          </>
        ) : isPopup ? (
          <>
            <div className="pbv-popup-image" aria-hidden="true">
              {imageUrl ? <img src={imageUrl} alt="" loading="lazy" /> : null}
            </div>
            <div className="pbv-popup-overlay" aria-hidden="true" />
            <div className="pbv-popup-content">
              {popupCode}
              {cta}
              {popupTrust}
            </div>
          </>
        ) : isHero ? (
          <>
            <div className="pbv-body">
              <p className="pbv-headline">{banner.Headline}</p>
              {banner.Subtext && (
                <p className="pbv-subtext">{banner.Subtext}</p>
              )}
              <div className="pbv-extras">{extras}</div>
              {cta}
            </div>
            {imageUrl && (
              <div className="pbv-media">
                <img src={imageUrl} alt="" loading="lazy" />
              </div>
            )}
          </>
        ) : (
          <>
            <p className="pbv-headline">{banner.Headline}</p>
            {banner.Subtext && <p className="pbv-subtext">{banner.Subtext}</p>}
            {extras}
            {cta}
          </>
        )}
        {closeButton}
      </div>
    </div>
  );
};

export default PromoBannerView;
